import React from 'react';
import moment from 'moment-timezone';

import Strings from '../../../Strings';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../../constants';
import { columnHelper } from '../../../containers/Table/Table3';
import { PERMISSIONS } from '../../../utils/userPermissions';
import GenericLineGraph from '../../../components/Graphs/GenericLineGraph';

export const root = 'device-invoices';

export const DeviceInvoicesTypes = {
  INITIAL: 'initial',
  RECURRING: 'recurring',
  KITTING: 'kitting',
  SHIPPING: 'shipping',
  DATA: 'data',
};

export const isDeviceBasedPricingType = pricingType => [
  DeviceInvoicesTypes.INITIAL,
  DeviceInvoicesTypes.KITTING,
  DeviceInvoicesTypes.SHIPPING,
  DeviceInvoicesTypes.DATA,
].includes(pricingType);

export const getPricingTypeLabel = pricingType => ({
  [DeviceInvoicesTypes.INITIAL]: Strings.deviceInvoices.initial,
  [DeviceInvoicesTypes.RECURRING]: Strings.deviceInvoices.recurring,
  [DeviceInvoicesTypes.KITTING]: Strings.deviceInvoices.kittingFee,
  [DeviceInvoicesTypes.SHIPPING]: Strings.deviceInvoices.fixedShippingFee,
  [DeviceInvoicesTypes.DATA]: Strings.deviceInvoices.dataFee,
}[pricingType] || pricingType);

export const DeviceInvoicesMainTabs = {
  INITIAL: {
    name: Strings.deviceInvoices.initial,
    key: 'initial',
    url: `/${root}/initial`,
    permission: PERMISSIONS.BILLING_DEVICE_INVOICES,
  },
  RECURRING: {
    name: Strings.deviceInvoices.recurring,
    key: 'recurring',
    url: `/${root}/recurring`,
    permission: PERMISSIONS.BILLING_DEVICE_INVOICES,
  },
  INVOICE_DATA: {
    name: Strings.deviceInvoices.invoiceData || 'Invoice Data',
    key: 'invoice-data',
    url: `/${root}/invoice-data`,
    permission: PERMISSIONS.BILLING_DEVICE_INVOICES,
  },
  PRICING: {
    name: Strings.deviceInvoices.pricing,
    key: 'pricing',
    url: `/${root}/pricing`,
    permission: PERMISSIONS.BILLING_DEVICE_INVOICES,
  },
};

export const INVOICE_STATUSES = Object.freeze({
  action_required: {
    key: 'action_required',
    name: Strings.deviceInvoices.actionRequired,
  },
  ready_for_approval: {
    key: 'ready_for_approval',
    name: Strings.deviceInvoices.readyForApproval,
  },
  approved: {
    key: 'approved',
    name: Strings.deviceInvoices.approved,
  },
  canceled: {
    key: 'canceled',
    name: Strings.deviceInvoices.canceled,
  },
});

export const initialRoot = `${root}/initial`;

export const DeviceInvoicesTabsDefinition = Object.keys(INVOICE_STATUSES).map(statusKey => ({
  key: INVOICE_STATUSES[statusKey].key,
  label: INVOICE_STATUSES[statusKey].name,
}));


export const PricingTabsDefinition = Object.keys(DeviceInvoicesTypes).map(statusKey => ({
  key: DeviceInvoicesTypes[statusKey],
  label: getPricingTypeLabel(DeviceInvoicesTypes[statusKey]),
}));

export function extractErrorMessage(resp) {
  return (
    resp?.response?.data?.standard_error?.message ||
    resp?.response?.data?.error?.message ||
    resp?.error ||
    Strings.deviceInvoicesActionFailed
  );
}

export function extractExceptionMessage(error) {
  return (
    error?.response?.data?.standard_error?.message ||
    error?.response?.data?.error?.message ||
    error?.message ||
    Strings.deviceInvoicesActionFailed
  );
}

export function formatTimestamp(value, format = DATE_TIME_SPACE_12_UPPERCASE) {
  if (!value) return '-';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(format) : '-';
}

export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '-';
  return `$${Number(value).toFixed(2)}`;
}

export const getGenerationSummaryMessage = (result) => {
  if (!result) return Strings.operationCompleted;
  const ret = ['created', 'updated', 'deleted', 'skipped', 'baselined'].filter(key => result[key]).map(key => `${Strings[key]}: ${result[key] || 0}`);
  return `${Strings.operationCompleted}: ${ret.join(', ')}`;
}

export function parseMoney(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function parseDate(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format() : undefined;
}

export function parseDateYMD(value) {
  if (value === '' || value === null || value === undefined) return undefined;
  if (moment.isMoment(value)) return value.isValid() ? value.format('YYYY-MM-DD') : undefined;
  const parsed = moment(value, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
}

export function normalizeInvoiceComment(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

export const getGraphDataForPriceHistory = (price, seriesName, dt) => {
  const ret = [];
  price.historicalPrices.forEach(p => {
    ret.push({ [seriesName]: p.price, [dt]: moment(p.effective_from) });
    if (p.effective_to) {
      ret.push({ [seriesName]: p.price, [dt]: moment(p.effective_to) });
    } else {
      ret.push({ [seriesName]: p.price, [dt]: moment() });
    }
  });
  ret.push({ [seriesName]: price.price, [dt]: moment(price.effective_from) });
  ret.push({ [seriesName]: price.price, [dt]: price.effective_to ? moment(price.effective_to) : moment() });
  return ret.sort((a, b) => a[dt].diff(b[dt]));
};

const getPricesHistoryGraph = (price) => {
  if (!price || !price.historicalPrices || price.historicalPrices.length === 0) return null;
  const sortedHistory = getGraphDataForPriceHistory(price, "price", "date");
  
  return (
    <div style={{ width: 150, height: 24 }}>
      <GenericLineGraph
        graphData={sortedHistory}
        hasBorder={false}
        dataSeries={[{
          name: 'Price',
          color: '#47d826',
          selector: item => item.price,
        }]}
        dateSelector={item => item.date}
        hasReferenceAreas={false}
        forceCondensed
        numericXAxis
        hasTooltip={false}
        hasAxes={false}
      />
    </div>
  );
};

export const getPricingColumns = (pricingType, onlyShowPriceAndDates = false) => {
    const ret = [];
    if (!onlyShowPriceAndDates) {
      ret.push(
        columnHelper.accessor('target', {
          header: isDeviceBasedPricingType(pricingType) ? Strings.deviceModel : Strings.program,
          cell: info => info.getValue() || '-',
          size: 280,
        })
      );
    }
    if (pricingType === DeviceInvoicesTypes.RECURRING && !onlyShowPriceAndDates) {
      ret.push(columnHelper.accessor('device_model_label', {
        header: Strings.deviceModel,
        cell: info => info.getValue() || '-',
        size: 220,
      }));
    }
    if (!onlyShowPriceAndDates) {
      ret.push(
        columnHelper.accessor('pricing_scope', {
          header: Strings.deviceInvoices.pricingScope,
          cell: info => info.getValue() || '-',
          size: 220,
        }),
        columnHelper.accessor('is_sample', {
          header: Strings.deviceInvoices.sample,
          cell: info => (info.getValue() ? Strings.yes : Strings.no),
          size: 120,
        }),
        columnHelper.accessor('price_history', {
          header: Strings.deviceInvoices.priceHistory,
          cell: info => getPricesHistoryGraph(info.row.original),
          size: 220,
        }),
      );
    }
    ret.push(
      columnHelper.accessor('price', {
        header: Strings.deviceInvoices.price,
        cell: info => (info.getValue() == null ? Strings.notSet : formatMoney(info.getValue())),
        size: 160,
      }),
      columnHelper.accessor('effective_from', {
        header: Strings.effectiveFrom,
        cell: info => info.getValue() || Strings.notSet,
        size: 180,
      }),
      columnHelper.accessor('effective_to', {
        header: Strings.effectiveTo,
        cell: info => info.getValue() || Strings.notSet,
        size: 180,
      }),
    );
    return ret;
};

export const validateInvoice = (invoiceData, commentRequired, isRecurring = false) => {
    const errors = [];
    if (commentRequired && !normalizeInvoiceComment(invoiceData?.comment)) {
      errors.push({ property: 'comment', propertyName: Strings.comment, missing: true, errors: [] });
    }
    if (isNaN(invoiceData?.price)) {
      errors.push({ property: 'price', propertyName: Strings.deviceInvoices.price, missing: true, errors: [] });
    }
    if (!isRecurring && isNaN(invoiceData?.shipping_fee)) {
      errors.push({ property: 'shipping_fee', propertyName: Strings.shipping.shippingCost, missing: true, errors: [] });
    }
    if (!isRecurring && isNaN(invoiceData?.kitting_fee)) {
      errors.push({ property: 'kitting_fee', propertyName: Strings.deviceInvoices.kittingFee, missing: true, errors: [] });
    }
    if (isRecurring && (invoiceData?.data_fee === '' || invoiceData?.data_fee == null || Number.isNaN(Number(invoiceData.data_fee)))) {
      errors.push({ property: 'data_fee', propertyName: Strings.deviceInvoices.dataFee, missing: true, errors: [] });
    }
    return errors;
}
